import React, { useState, useEffect, useMemo } from "react";
import { v4 as uuidV4 } from "uuid";

import styles from "./ManyPhotosDropzone.module.css";

// Dropzone
import { useDropzone } from "react-dropzone";
// Dropzone styles
import {
  baseStyle,
  focusedStyle,
  acceptStyle,
  rejectStyle,
} from "./DropzoneStyles";

// Helpers
import { ScrollDisable } from "../../helpers/ScrollLock";
import cloneDeep from "lodash.clonedeep";
import { getImageSrc, handleImageLoad } from "../../helpers/imageLoadUtils";
import toastManager from "../../helpers/toastManager";

// Custom components
import {
  SquareXButton,
  TextUtilityButton,
  DropzoneButton,
} from "../Buttons/Buttons";

const ManyPhotosDropzoneImage = ({
  props,
  index,
  file,
  imagesLoading,
  setImagesLoading,
}) => {
  const [imageSrc, setImageSrc] = useState("");
  useEffect(() => {
    setImageSrc(getImageSrc(file));
  }, []);

  return (
    <img
      src={imageSrc}
      draggable={false}
      className=" object-scale-down 
              transition-all duration-200
              cursor-pointer hover:scale-105 active:scale-100
              w-full h-full
              max-w-[25rem] max-h-[25rem]
              "
      onLoad={(e) =>
        handleImageLoad({
          imageElement: e.target,
          setItemLoading: setImagesLoading((previousState) => {
            let updatedImagesLoading = [...previousState];
            updatedImagesLoading[index] = false;
            return updatedImagesLoading;
          }),
        })
      }
      onClick={() => {
        ScrollDisable();
        props.setModalImage(file);
      }}
      style={
        !imagesLoading[index]
          ? {}
          : {
              position: "absolute",
              opacity: "0",
            }
      }
    />
  );
};

// Preview Dropzone
function ManyPhotosDropzone(props) {
  const maxImages = 10;

  // Create array of 10 elements with each value being false
  const [imagesLoading, setImagesLoading] = useState(Array(10).fill(true));

  function maxFiles(files) {
    if (props.files.length >= maxImages) {
      toastManager(
        "warning",
        "Image Limit Exceeded!",
        `Cannot add any more images to entry. Image limit is ${maxImages}.`
      );
      return 1;
    } else {
      return null;
    }
  }

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({
    accept: {
      "image/*": [],
    },
    noClick: true,
    validator: maxFiles,

    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length + props.files.length <= maxImages) {
        props.setEntryImages([
          ...props.entryImages,
          ...acceptedFiles.map((file) => file),
        ]);
        props.setFiles([
          ...props.files,
          ...acceptedFiles.map((file, index) => {
            const uniqueKey = `${uuidV4()}-${index}`;
            return Object.assign(file, {
              uniqueKey: uniqueKey,
            });
          }),
        ]);

        // Append file details when files are added
        const updatedFilesDetails = cloneDeep(props.filesDetails);
        for (let i = 0; i < acceptedFiles.length; i++) {
          updatedFilesDetails.push({ caption: "" });
        }
        props.setFilesDetails(updatedFilesDetails);
      } else {
        toastManager(
          "warning",
          "Image Limit Exceeded!",
          `Adding ${
            acceptedFiles.length
          } more images will exceed the maximum limit of ${maxImages} images per entry. You can only add ${
            maxImages - props.files.length
          } more images.`
        );
      }
    },
  });

  const removeFile = (file) => () => {
    // Clear file and file details at index on deletion
    const newFiles = [...props.files];
    const fileIndex = newFiles.indexOf(file);

    newFiles.splice(fileIndex, 1);
    props.setFiles(newFiles);

    const updatedFilesDetails = cloneDeep(props.filesDetails);
    updatedFilesDetails.splice(fileIndex, 1);
    props.setFilesDetails(updatedFilesDetails);
  };

  const removeAll = () => {
    props.setFiles([]);
    props.setFilesDetails([]);
  };

  const imageName = (file) => {
    if (file.previousEntryItem) {
      return file.file.name;
    } else if (file.apiItem) {
      return file.name;
    } else if (!file.apiItem) {
      return file.path;
    }
  };

  const thumbs = props.files.map((file, index) => (
    <div
      key={file.uniqueKey}
      className="flex flex-col items-center justify-center p-2 Xbg-red-500
      h-[35rem]
      w-full md:w-1/2 lg:w-1/2 xl:w-1/3
      "
    >
      <div
        className="flex flex-col items-center justify-between p-2 rounded-xl overflow-hidden Xbg-blue-500
        outline outline-1
        w-full h-full"
      >
        <div className="flex flex-row items-center justify-end w-full Xbg-purple-500">
          <SquareXButton actionFunction={removeFile(file)} />
        </div>
        <div className="flex flex-1 flex-col items-center justify-between w-full Xbg-red-500">
          <div className="flex flex-col items-center justify-center h-full w-full p-2 Xbg-orange-500">
            {imagesLoading[index] && (
              <div className="flex flex-col items-center justify-center rounded-xl">
                <div className={styles["loader"]} />
              </div>
            )}
            <ManyPhotosDropzoneImage
              props={props}
              index={index}
              file={file}
              imagesLoading={imagesLoading}
              setImagesLoading={setImagesLoading}
            />
          </div>

          <div className="flex flex-col items-center justify-center w-full Xbg-green-500">
            <div className="flex flex-row items-center justify-start w-full py-1 Xbg-orange-500">
              <div className="flex flex-[3] flex-row items-center justify-start overflow-x-scroll">
                <p className="text-base font-normal text-neutral-900">
                  {imageName(file)}
                </p>
              </div>
              <div className="flex flex-[2] flex-row items-center justify-end">
                <p className="text-base font-normal text-neutral-900">
                  {file.previousEntryItem ? (
                    <>{Math.round((file.file.size / 1000000) * 100) / 100} MB</>
                  ) : (
                    <>{Math.round((file.size / 1000000) * 100) / 100} MB</>
                  )}
                </p>
              </div>
            </div>

            <div className="flex flex-row w-full">
              <input
                className="flex w-full items-center p-2 border border-neutral-300 rounded-lg
                           focus:outline-none focus:ring-inset focus:ring-neutral-300
                           text-base font-normal text-neutral-900
                           "
                placeholder={`Figure ${index + 1}.`}
                value={props.filesDetails[index].caption}
                onChange={(event) => {
                  const dataCopy = cloneDeep(props.filesDetails);
                  dataCopy[index].caption = event.target.value;
                  props.setFilesDetails(dataCopy);
                }}
                maxLength={50}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  ));

  // Dynamically change style of dropzone depending on what is happening
  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () =>
      props.files.forEach((file) => URL.revokeObjectURL(file.preview));
  }, []);

  return (
    <div className="flex flex-col items-center justify-center w-full h-full Xbg-pink-500">
      <div className="flex flex-row items-start justify-between w-full h-16 Xbg-green-500">
        <TextUtilityButton
          prompt={"Browse Entries"}
          actionFunction={() => {
            props.setEntryBrowserModalOpen(true);
            ScrollDisable();
          }}
        />

        <TextUtilityButton prompt={"Select Images"} actionFunction={open} />
      </div>

      <div {...getRootProps({ className: "dropzone", style })}>
        <input {...getInputProps()} />

        {props.files.length === 0 && <DropzoneButton openFunction={open} />}

        {thumbs.length > 0 && (
          <div className="flex flex-row flex-wrap items-center justify-center w-full h-full Xbg-green-500">
            {thumbs}
          </div>
        )}
      </div>

      {props.files.length > 0 && (
        <div className="flex flex-row items-center justify-end w-full pt-3">
          <TextUtilityButton prompt={"Remove All"} actionFunction={removeAll} />
        </div>
      )}
    </div>
  );
}

export default ManyPhotosDropzone;
